/**
 * Centralized API Configuration
 * 
 * This file provides a single source of truth for all API endpoints and URLs.
 * All services should import from this file instead of using hardcoded URLs.
 */

export const API_CONFIG = {
    /**
     * WordPress Backend Configuration
     */
    wordpress: {
        /**
         * WordPress REST API base URL
         * Default: https://vatli365.vn/wp-json/qoder/v1
         */
        apiUrl: import.meta.env.VITE_WP_API_URL || 'https://vatli365.vn/wp-json/qoder/v1',

        /**
         * WordPress uploads directory URL
         * Default: https://www.vatli365.vn/wp-content/uploads
         */
        uploadsUrl: import.meta.env.VITE_WP_UPLOADS_URL || 'https://www.vatli365.vn/wp-content/uploads',
    },

    /**
     * Python Backend Configuration (New)
     */
    python: {
        /**
         * Python FastAPI backend URL
         * Development: http://localhost:8000
         * Production: /api (Relative path via Nginx)
         */
        apiUrl: import.meta.env.DEV
            ? (import.meta.env.VITE_PYTHON_API_URL || 'http://localhost:8000')
            : '/api',
    },

    /**
     * Bank Data Configuration (Questions, Images, etc.)
     */
    bank: {
        /**
         * Bank data base URL (hosted on local server)
         * Development: /bank
         * Production: https://ebook.vatli365.vn/bank
         * FORCE: Ignore VITE_BANK_URL to ensure we never accidentally use the old main server
         */
        baseUrl: import.meta.env.DEV ? '/bank' : 'https://ebook.vatli365.vn/bank',
    },

    /**
     * Video Configuration (Simulation Videos)
     */
    videos: {
        /**
         * Video base URL
         * Development: /videos (local public folder)
         * Production: https://ebook.vatli365.vn/videos (same-origin for best performance)
         */
        baseUrl: import.meta.env.VITE_VIDEOS_URL || (import.meta.env.DEV ? '/videos' : 'https://ebook.vatli365.vn/videos'),
    },

    /**
     * OpenAI Proxy Configuration
     */
    openai: {
        /**
         * OpenAI proxy URL (hosted on WordPress server for security)
         * Default: https://vatli365.vn/openai-proxy.php
         * Note: In DEV, we use /openai-proxy to bypass CORS via Vite proxy
         */
        /**
         * OpenAI proxy URL (New: Points to Python Backend)
         * Development: http://localhost:8000/ai/explain
         * Production: /api/ai/explain
         */
        proxyUrl: import.meta.env.DEV ? 'http://localhost:8000/ai/explain' : '/api/ai/explain',
    },

    /**
     * Google OAuth Configuration
     */
    google: {
        /**
         * Google OAuth Client ID
         */
        clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '167617427377-gp0dvsi9aat9dfute1c7gl591eb0e7r7.apps.googleusercontent.com',

        /**
         * Google OAuth Redirect URI
         * Development: http://localhost:5173/auth/google/callback
         * Production: https://ebook-vatli365.vercel.app/auth/google/callback (or custom domain)
         */
        redirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI ||
            (typeof window !== 'undefined'
                ? `${window.location.origin}/auth/google/callback`
                : (import.meta.env.PROD
                    ? 'https://ebook.vatli365.vn/auth/google/callback'
                    : 'http://localhost:5173/auth/google/callback')),
    },

    /**
     * App Configuration
     */
    app: {
        /**
         * App environment
         */
        env: import.meta.env.VITE_APP_ENV || (import.meta.env.DEV ? 'development' : 'production'),

        /**
         * App base URL
         */
        url: import.meta.env.VITE_APP_URL ||
            (import.meta.env.DEV
                ? 'http://localhost:5173'
                : 'https://ebook.vatli365.vn'), // Will update to Vercel domain
    },

    /**
     * Feature Flags
     */
    features: {
        /**
         * Enable learning history database
         */
        learningHistory: import.meta.env.VITE_ENABLE_LEARNING_HISTORY_DB === 'true',

        /**
         * Use Python backend instead of WordPress
         * Set VITE_USE_PYTHON_BACKEND=true to enable
         */
        usePythonBackend: true, // Forced to true per unified backend transition
    },
};

/**
 * Helper function to get full API endpoint URL
 */
export const getApiEndpoint = (path: string): string => {
    // Remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${API_CONFIG.wordpress.apiUrl}/${cleanPath}`;
};

/**
 * Helper function to get WordPress uploads URL
 */
export const getUploadsUrl = (path: string): string => {
    // Remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${API_CONFIG.wordpress.uploadsUrl}/${cleanPath}`;
};

/**
 * Log configuration on initialization (only in development)
 */
if (import.meta.env.DEV) {
    console.log('📋 API Configuration:', {
        wordpress: API_CONFIG.wordpress,
        openai: { proxyUrl: API_CONFIG.openai.proxyUrl },
        google: {
            clientId: API_CONFIG.google.clientId.substring(0, 20) + '...',
            redirectUri: API_CONFIG.google.redirectUri
        },
        app: API_CONFIG.app,
        features: API_CONFIG.features,
    });
}

export default API_CONFIG;
